"use client";

import { useState, type FormEvent } from "react";

type Rules<K extends string> = Partial<Record<K, (value: string) => string | undefined>>;

/**
 * Minimal client-side form state: values, per-field errors shown after blur or
 * submit, and focus moved to the first invalid field on a failed submit.
 * Field `name` attributes must match the keys of `initial`.
 */
export function useForm<K extends string>(initial: Record<K, string>, rules: Rules<K>, onValid: () => void) {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState<Partial<Record<K, string>>>({});
  const [touched, setTouched] = useState<Partial<Record<K, boolean>>>({});

  const check = (key: K, value: string) => rules[key]?.(value);

  const bind = (key: K) => ({
    name: key,
    value: values[key],
    onChange: (e: { target: { value: string } }) => {
      const value = e.target.value;
      setValues((v) => ({ ...v, [key]: value }));
      if (touched[key]) setErrors((er) => ({ ...er, [key]: check(key, value) }));
    },
    onBlur: () => {
      setTouched((t) => ({ ...t, [key]: true }));
      setErrors((er) => ({ ...er, [key]: check(key, values[key]) }));
    },
    "aria-invalid": errors[key] ? true : undefined,
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const keys = Object.keys(initial) as K[];
    const next: Partial<Record<K, string>> = {};
    keys.forEach((k) => {
      next[k] = check(k, values[k]);
    });
    setErrors(next);
    setTouched(Object.fromEntries(keys.map((k) => [k, true])) as Record<K, boolean>);
    const firstBad = keys.find((k) => next[k]);
    if (firstBad) {
      const el = e.currentTarget.elements.namedItem(firstBad);
      if (el instanceof HTMLElement) el.focus();
      return;
    }
    onValid();
  };

  const reset = () => {
    setValues(initial);
    setErrors({});
    setTouched({});
  };

  return { values, errors, bind, onSubmit, reset };
}
